import { useQuery } from '@apollo/react-hooks';

import { getGameListItem } from './apiQueries';
import { Airing, Fer, Highlight } from '../../utils/types';

const AUTO_REFETCH_PERIOD = 30 * 1000; // 30 seconds in milliseconds

const sortByChannelName = <T extends { channel?: { name?: string | null } | null }>(items: T[]): T[] =>
	[...items].sort((a, b): number => {
		if (!a.channel?.name || !b.channel?.name) return 0;
		return a.channel.name > b.channel.name ? 1 : -1;
	});

const getMaxClipsForAiring = (airing: Airing): number => {
	if (!airing.highlights?.items) return 0;

	let maxClips = 0;
	airing.highlights.items.forEach((highlight: Highlight) => {
		if (highlight.type !== 'LIVE' || !highlight.periods?.items?.length) return;
		if (highlight.periods.items.length > maxClips) maxClips = highlight.periods.items.length;
	});

	return maxClips;
};

type GameListItemData = {
	loading: boolean;
	error: unknown;
	airings: Airing[];
	fers: Fer[];
	clipsForAiring: (airing: Airing) => number;
};

export const useGameListItemData = (gameId: string, gameStatus?: string | null): GameListItemData => {
	const isFastRefreshStatus = gameStatus && ['LIVE', 'IN_PROGRESS', 'COMPLETED'].includes(gameStatus);
	const { loading, error, data } = useQuery(getGameListItem, {
		variables: { id: gameId },
		pollInterval: isFastRefreshStatus ? AUTO_REFETCH_PERIOD : 0
	});

	const airings: Airing[] = data?.getGame?.airings?.items ? sortByChannelName(data.getGame.airings.items) : [];
	const fers: Fer[] = data?.listFers?.items
		? sortByChannelName(data.listFers.items.filter((item: Fer & { __typename?: string }): boolean => item.__typename === 'Fer'))
		: [];

	return {
		loading,
		error,
		airings,
		fers,
		clipsForAiring: getMaxClipsForAiring
	};
};
